/**
 * Recorte del media de un área en Pixi a partir de `area.media.clipPath` (el
 * mismo string CSS que el DOM aplica al `<video>`/`<img>` del editor). Sin esto
 * el stream enmascaraba todas las áreas con un rect: la cámara circular de una
 * plantilla (p. ej. Aurora Night, `circle(50%)`) salía cuadrada en el directo.
 *
 * Soporta SOLO `circle()` con radio en `%` o `closest-side`/`farthest-side` y
 * centro en `%`/`center`/`left|right|top|bottom` — lo que traen las plantillas.
 * Los px de CSS no se soportan (son px del editor, no del canvas del stream) y
 * cualquier otra forma (`inset`, `polygon`, `ellipse`, `path`) devuelve null →
 * el caller mantiene el rect de siempre.
 */

export interface ClipCircle {
  kind: "circle";
  cx: number;
  cy: number;
  r: number;
}

export interface MediaBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const POSITION_KEYWORDS: Record<string, number> = {
  left: 0,
  top: 0,
  center: 50,
  right: 100,
  bottom: 100,
};

const parsePercent = (token: string | undefined): number | null => {
  if (token === undefined) return null;
  if (token in POSITION_KEYWORDS) return POSITION_KEYWORDS[token];
  const m = /^(-?\d+(?:\.\d+)?)%$/.exec(token);
  return m ? Number(m[1]) : null;
};

export const parseAreaClipCircle = (
  clipPath: string | undefined,
  box: MediaBox,
): ClipCircle | null => {
  if (!clipPath || box.width <= 0 || box.height <= 0) return null;

  const m = /^\s*circle\(\s*([^)]*?)\s*\)\s*$/i.exec(clipPath);
  if (!m) return null;

  const [radiusPart, positionPart] = m[1].toLowerCase().split(/\s*\bat\b\s*/);
  const position = (positionPart ?? "").split(/\s+/).filter(Boolean);

  let px = 50;
  let py = 50;
  if (position.length === 1) {
    const p = position[0];
    const v = parsePercent(p);
    if (v === null) return null;
    // Una sola palabra vertical fija Y; el resto fija X (como CSS).
    if (p === "top" || p === "bottom") py = v;
    else px = v;
  } else if (position.length >= 2) {
    const vx = parsePercent(position[0]);
    const vy = parsePercent(position[1]);
    if (vx === null || vy === null) return null;
    px = vx;
    py = vy;
  }

  const cx = (px / 100) * box.width;
  const cy = (py / 100) * box.height;

  const closest = Math.min(cx, box.width - cx, cy, box.height - cy);
  const farthest = Math.max(cx, box.width - cx, cy, box.height - cy);

  const radiusToken = radiusPart.trim();
  let r: number;
  if (radiusToken === "" || radiusToken === "closest-side") {
    r = closest;
  } else if (radiusToken === "farthest-side") {
    r = farthest;
  } else {
    const pct = /^(\d+(?:\.\d+)?)%$/.exec(radiusToken);
    if (!pct) return null;
    // CSS: el % del radio es sobre sqrt(w² + h²) / sqrt(2).
    r =
      (Number(pct[1]) / 100) *
      (Math.hypot(box.width, box.height) / Math.SQRT2);
  }

  if (!(r > 0)) return null;

  return { kind: "circle", cx: box.x + cx, cy: box.y + cy, r };
};
